import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Button,
  IconButton,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "650px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "2px",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    marginTop: "3px",
    marginLeft: "2rem",
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  resetButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
}));

const DocumentGroupsTab = (props) => {
  const { handleNext, handleBack, setDocumentGroup, featuresAccess } = props;
  const classes = useStyles();
  const [documentGroups, setDocumentGroups] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);

  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const fetchDocumentGroups = async () => {
    setLoading(true);
    setError("");
    console.log("Fetching document groups...");
    try {
      // projectConfig = false for sponsor network UI
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchDocumentGroups/false`;
      
      const response = await axios.get(url);
      
      if (response.data?.status === true || response.data?.status === "true") {
        const documentGroupsList = response.data?.data?.documentGroups || [];
        
        // Map the API response to match the expected structure
        const mappedDocumentGroups = documentGroupsList.map((doc) => ({
          id: doc.documentGroupID,
          name: doc.documentGroupName,
          selected: !!doc.activated,
          isNew: false,
          type: doc.documentGroupType,
          lastUpdatedBy: doc.lastUpdatedBy,
        }));

        setDocumentGroups(mappedDocumentGroups);
      } else {
        console.error("Failed to fetch document groups:", response.data?.message);
        setError(response.data?.message || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching document groups:", error);
      console.error("Error details:", error.response?.data);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocumentGroups();
  }, []);

  const saveDocumentGroup = async (index) => {
    if (savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const groupToSave = documentGroups[index];
      if (!groupToSave.name.trim()) {
        setError("Please provide a name for the document group.");
        setSavingStates((prev) => ({ ...prev, [index]: false }));
        return;
      }

      // Prepare the payload for the API
      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        documentGroups: [
          {
            documentGroupName: groupToSave.name.trim(),
            documentGroupType: "New",
            activated: groupToSave.selected, // Use the actual selected value from toggle
          },
        ],
      };

      console.log("Saving document group with payload:", payload);

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addDocumentGroups`;
      const response = await axios.post(url, payload);
      
      if (response.data?.status === true || response.data?.status === "true") {
        fetchDocumentGroups();
        if (setDocumentGroup) {
          setDocumentGroup((prev) => !prev);
        }
        setDocumentGroups((prev) =>
          prev.map((doc, i) =>
            i === index
              ? { ...doc, id: response.data?.data?.documentGroupID, isNew: false, type: "New" }
              : doc
          )
        );
      } else {
        console.error("Failed to save document group:", response.data?.message);
        setError(response.data?.message || "Failed to save document group.");
      }
    } catch (error) {
      console.error("Error saving document group:", error);
      setError("An error occurred while saving the document group.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleGroupToggle = (index) => {
    const newList = [...documentGroups];
    newList[index].selected = !newList[index].selected;
    newList[index].isUpdated = true;
    setDocumentGroups(newList);
    setIsUpdated(true);
  };

  const handleGroupNameChange = (index, value) => {
    const newList = [...documentGroups];
    newList[index].name = value; // Don't trim during typing, allow spaces and parentheses
    setDocumentGroups(newList);
    setIsUpdated(true);
  };

  const handleAddNewGroup = () => {
    setDocumentGroups([
      ...documentGroups,
      {
        name: "",
        selected: false,
        isNew: true,
        type: "New",
      },
    ]);
    setIsUpdated(true);
  };

  const handleGroupDelete = async (index) => {
    try {
      const groupToDelete = documentGroups[index];
      
      if (groupToDelete.isNew) {
        setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
        return;
      }
      
      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        documentGroups: [
          {
            documentGroupID: groupToDelete.id,
            documentGroupName: groupToDelete.name?.trim() || groupToDelete.name,
            documentGroupType: groupToDelete.type,
            activated: groupToDelete.selected,
            markForDelete: true,
          },
        ],
      };

      console.log("Deleting document group with payload:", payload);

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDocumentGroups`;
      const response = await axios.post(url, payload);
      
      if (response.data?.status === true || response.data?.status === "true") {
        fetchDocumentGroups();
        if (setDocumentGroup) {
          setDocumentGroup((prev) => !prev);
        }
        setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
      } else {
        console.error(
          "Failed to delete document group:",
          response.data?.message
        );
        setError(response.data?.message || "Failed to delete document group.");
      }
    } catch (error) {
      console.error("Error deleting document group:", error);
      setError("An error occurred while deleting the document group.");
    }
  };

  const handleGroupCancel = (index) => {
    const groupToCancel = documentGroups[index];

    if (groupToCancel.isNew) {
      setDocumentGroups((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    setDocumentGroups((prev) =>
      prev.map((doc, i) =>
        i === index
          ? {
              ...doc,
              name: doc.originalName || doc.name,
              selected: doc.originalSelected || doc.selected,
              isUpdated: false,
            }
          : doc
      )
    );
  };

  const handleSaveSelection = async () => {
    try {
      setSaveLoading(true);
      const isAnyToggleOn = documentGroups.filter((doc) => doc.isUpdated);

      if (!isAnyToggleOn.length) {
        setSaveLoading(false);
        return;
      }
      
      if (documentGroups.every((doc) => !doc.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        setSaveLoading(false);
        return;
      }
      setErrorMessage("");

      if (isUpdated) {
        const payload = {
          sponsorId: 2,
          ecosystemId: 1,
          documentGroups: isAnyToggleOn.map((doc) => ({
            documentGroupID: doc.id,
            documentGroupName: doc.name?.trim() || doc.name,
            documentGroupType: doc.type,
            activated: doc.selected,
            markForDelete: false,
          })),
        };

        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateDocumentGroups`;
        const response = await axios.post(url, payload);

        if (
          response.data?.status === true ||
          response.data?.status === "true"
        ) {
          setIsUpdated(false);
          fetchDocumentGroups();
          setErrorMessage("");
        } else {
          setErrorMessage(
            response.data?.message || "Failed to update document groups"
          );
        }
      }
    } catch (error) {
      console.error("Error saving document groups:", error);
      setErrorMessage("An error occurred while saving. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleResetSelection = () => {
    if (isUpdated) {
      fetchDocumentGroups();
      setIsUpdated(false);
      setErrorMessage("");
    }
  };

  return (
    <div>
      <div
        className={classes.container}
        style={{ display: "flex", flexDirection: "column", gap: "24px" }}
      >
        {/* Table and Buttons Section */}
        <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
          {/* Document Groups Table */}
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Selection</TableCell>
                <TableCell className={classes.headerCell}>Document Group</TableCell>
                <TableCell className={classes.headerCell}>
                  Document Category ID
                </TableCell>
                <TableCell className={classes.headerCell}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60px",
                        backgroundColor: "#ffecec",
                        color: "#b00020",
                        border: "1px solid #ff9999",
                      }}
                    >
                      <Typography>{error}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : documentGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60px",
                        color: "#666",
                      }}
                    >
                      <Typography>No Document Groups Found</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                documentGroups.map((doc, index) => (
                  <TableRow key={doc.id || `new-${index}`}>
                    <TableCell className={classes.bodyCell}>
                      <Switch
                        checked={doc.selected}
                        onChange={() => handleGroupToggle(index)}
                        classes={{ switchBase: classes.switchBase }}
                        disabled={!canEdit}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {doc.isNew ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <TextField
                            placeholder="Type Text Here"
                            value={doc.name}
                            onChange={(e) =>
                              handleGroupNameChange(index, e.target.value)
                            }
                            onBlur={(e) =>
                              handleGroupNameChange(index, e.target.value.trim())
                            }
                            className={classes.textField}
                            variant="outlined"
                            inputProps={{
                              onBeforeInput: (e) => {
                                // Allow letters, numbers, spaces, hyphens, forward slashes, parentheses, and ampersands
                                const allowedChars = /^[a-zA-Z0-9\s\-/()&]+$/;
                                if (e.data && !allowedChars.test(e.data)) {
                                  e.preventDefault();
                                }
                              }
                            }}
                          />
                          <div className={classes.inlineActions}>
                            <IconButton
                              onClick={() => handleGroupCancel(index)}
                              className={classes.iconBtn}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                saveDocumentGroup(index);
                              }}
                              className={classes.iconBtn}
                              disabled={
                                !doc.name?.trim() ||
                                documentGroups.some(
                                  (d, i) =>
                                    i !== index &&
                                    d.name?.trim().toLowerCase() === doc.name?.trim().toLowerCase()
                                ) || savingStates[index]
                              }
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        doc.name
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {doc.isNew ? "" : doc?.id}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {canEdit && (doc?.isNew === true || doc?.type === "New") && (
                        <IconButton onClick={() => handleGroupDelete(index)}>
                          <DeleteIcon className={classes.deleteIcon} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className={classes.buttonContainer}>
            <Button
              className={classes.saveButton}
              type="button"
              onClick={handleSaveSelection}
              disabled={!isUpdated || saveLoading || !canEdit}
              style={{
                opacity: !isUpdated || !canEdit ? 0.7 : 1,
                color: "#fff", 
              }}
            >
              {saveLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  Saving...
                  <CircularProgress size={16} style={{ color: '#fff' }} />
                </div>
              ) : (
                "Save Selection"
              )}
            </Button>

            <Button
              className={classes.resetButton}
              type="button"
              onClick={handleResetSelection}
              disabled={!isUpdated || !canEdit}
              style={{
                opacity: !isUpdated || !canEdit ? 0.8 : 1,
              }}
            >
              Reset Selection
            </Button>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}

      {canEdit && (
        <Button onClick={handleAddNewGroup} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Document Group
        </Button>
      )}
    </div>
  );
};

export default DocumentGroupsTab;

